import { OutboundShipmentKind } from "@redotech/congo-enums/outbound-shipment-kind";
import { OutboundShipmentSource } from "@redotech/congo-enums/outbound-shipment-source";
import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { Currency } from "@redotech/money/currencies";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { CarrierSchema } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { HazmatSchema } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import {
  ConsigneeType,
  ContentsType,
  NonDeliveryOption,
  OutboundShipmentTaxIdSchema,
  USPSExemptionCode,
} from "@redotech/redo-fulfillment-customs-common/customs";
import { LandedCostBillingSchema } from "@redotech/redo-fulfillment-customs-common/landed-cost-buy-options";
import {
  LengthSchema,
  MoneySchema,
  WeightSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { OutboundShipmentParcelV2Schema } from "@redotech/redo-fulfillment-parcel-common/outbound-shipment-parcel-v2";
import { CustomParcelType } from "@redotech/redo-fulfillment-parcel-common/parcel";
import * as zExt from "@redotech/rpc/ext";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import type { ProviderInfo, ShipmentPaymentOption } from "./outbound-labels";
import {
  CanadaPostOption,
  RateProvider,
  rawOutboundRateSchema,
  ShipmentPaymentOptionSchema,
  ShipmentRefundStatus,
  SpecialRateEligibility,
} from "./outbound-labels";

export enum TiktokShippingType {
  /** Tiktok provides shipping label */
  TIKTOK = "TIKTOK",
  /** Seller obtains shipping label and uploads tracking number to Tiktok */
  SELLER = "SELLER",
}

export enum TiktokShippingTypeTag {
  TIKTOK = "Shipping Type: Tiktok",
  SELLER = "Shipping Type: Seller",
}

export enum DhlEcommerceServiceEndorsement {
  AddressForwardingRequested = "1",
  ForwardingServiceRequested = "2",
  ChangeServiceRequested = "3",
  ReturnServiceRequested = "4",
}

export const DhlEcommerceServiceEndorsementSchema = createEnum(
  DhlEcommerceServiceEndorsement,
  { enumName: "DhlEcommerceServiceEndorsement" },
).meta({ privacy: privacy.nonPii });

const dhlEcommerceLabelOptionsSchema = z
  .object({
    customLabelText1: z
      .string()
      .meta({ privacy: privacy.text.merchant })
      .nullish(),
    customLabelText2: z
      .string()
      .meta({ privacy: privacy.text.merchant })
      .nullish(),
    serviceEndorsement: DhlEcommerceServiceEndorsementSchema.nullish().meta({
      privacy: privacy.nonPii,
    }),
  })
  .meta({ privacy: privacy.structured });
export const DhlEcommerceLabelOptionsSchema: z.ZodType<DhlEcommerceLabelOptions> =
  dhlEcommerceLabelOptionsSchema;
export interface DhlEcommerceLabelOptions extends z.infer<
  typeof dhlEcommerceLabelOptionsSchema
> {}

// DHL partnership certification teams. These teams authenticate against DHL's
// certification environment (DHL_ECOMMERCE_CERTIFICATION_* credentials) and get
// access to the certification label options (custom label text, ancillary
// endorsement, weight-based direct return labels).
export const DHL_CERTIFICATION_TEAM_IDS = new Set<string>([
  "6a1db6a84ef1324b6e44d52b", // production DHL certification store
  "6a1dc49f3b6b1095b893011d", // local DHL test instance
  "69efe038e4f1a01e6b84d8a7", // Coleman's DHL direct-return team
]);

export const isDhlCertificationTeam = (
  teamId: string | null | undefined,
): boolean => !!teamId && DHL_CERTIFICATION_TEAM_IDS.has(teamId);

export const outboundShipmentAddressSchema = z
  .object({
    street1: z
      .string()
      .meta({ privacy: privacy.address_line.customer })
      .nullish(),
    street2: z
      .string()
      .meta({ privacy: privacy.address_line.customer })
      .nullish(),
    city: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    state: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    zip: z.string().meta({ privacy: privacy.geo.coarse }),
    country: z.string().meta({ privacy: privacy.geo.coarse }),
    residential: z.boolean().meta({ privacy: privacy.flag }).nullish(),
    name: z.string().meta({ privacy: privacy.person_name.customer }).nullish(),
    company: z
      .string()
      .meta({ privacy: privacy.company_name.customer })
      .nullish(),
    phone: z.string().meta({ privacy: privacy.phone.customer }).nullish(),
    email: z.string().meta({ privacy: privacy.email.customer }).nullish(),
    consigneeType: createEnum(ConsigneeType, { enumName: "ConsigneeType" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

export interface OutboundShipmentAddress extends z.infer<
  typeof outboundShipmentAddressSchema
> {}

export const outboundShipmentAddressesEqual = (
  a: OutboundShipmentAddress,
  b: OutboundShipmentAddress,
) => {
  for (const key of Object.keys(a)) {
    if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      a[key as keyof OutboundShipmentAddress] !==
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      b[key as keyof OutboundShipmentAddress]
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Provider-neutral manufacturer info for carriers that require it on customs
 * items (e.g. Chit Chats U.S. DDP). `manufacturer` is the company / contact.
 */
// EU Product Identifiers (Reg. 2026/1022) carried per customs item.
const euProductIdentifierFields = {
  merchant_product_id: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  manufacturer_product_id: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  standardized_product_id: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
};

const manufacturerCustomsFields = {
  manufacturer: z
    .string()
    .meta({ privacy: privacy.company_name.merchant })
    .nullish(),
  manufacturer_mid: z
    .string()
    .meta({ privacy: privacy.id.government })
    .nullish(),
  manufacturer_street: z
    .string()
    .meta({ privacy: privacy.address_line.merchant })
    .nullish(),
  manufacturer_street_2: z
    .string()
    .meta({ privacy: privacy.address_line.merchant })
    .nullish(),
  manufacturer_city: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
  manufacturer_state: z
    .string()
    .meta({ privacy: privacy.geo.coarse })
    .nullish(),
  manufacturer_postal_code: z
    .string()
    .meta({ privacy: privacy.geo.coarse })
    .nullish(),
  manufacturer_country: z
    .string()
    .meta({ privacy: privacy.geo.coarse })
    .nullish(),
};

const OutboundShipmentCustomsItemSchema = z.object({
  object: z.string().meta({ privacy: privacy.enumOrStatus }),
  description: z.string().meta({ privacy: privacy.productCatalogText }),
  quantity: z.number().meta({ privacy: privacy.quantity }),
  origin_country: z.string().meta({ privacy: privacy.geo.coarse }),
  hs_tariff_number: z.string().meta({ privacy: privacy.id.external.resource }),
  // This is total value
  value: z.string().meta({ privacy: privacy.money }),
  unit_value: z.string().meta({ privacy: privacy.money }).nullish(),
  currency: createEnum(Currency, { enumName: "Currency" }).nullish(),
  weight: z.number().meta({ privacy: privacy.dimension }),
  code: z.string().meta({ privacy: privacy.productSku }).nullish(),
  ...manufacturerCustomsFields,
  product_identifier: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  ...euProductIdentifierFields,
});

export interface OutboundShipmentCustomsItem extends z.infer<
  typeof OutboundShipmentCustomsItemSchema
> {}

const OutboundShipmentCustomsInfoSchema_ = z
  .object({
    customs_info: z
      .object({
        object: z.string().meta({ privacy: privacy.enumOrStatus }),
        contents_type: z.string().meta({ privacy: privacy.enumOrStatus }),
        contents_explanation: z
          .string()
          .meta({ privacy: privacy.text.merchant })
          .nullish(),
        additional_customs_comments: z
          .string()
          .meta({ privacy: privacy.text.merchant })
          .nullish(),
        customs_certify: z.boolean().meta({ privacy: privacy.flag }),
        customs_signer: z
          .string()
          .meta({ privacy: privacy.person_name.merchant }),
        eel_pfc: z.string().meta({ privacy: privacy.enumOrStatus }),
        non_delivery_option: z.string().meta({ privacy: privacy.enumOrStatus }),
        restriction_type: z
          .literal("none")
          .meta({ privacy: privacy.enumOrStatus }),
        customs_items: z
          .array(
            OutboundShipmentCustomsItemSchema.meta({
              privacy: privacy.structured,
            }),
          )
          .meta({ privacy: privacy.structured }),
        declaration: z
          .string()
          .meta({ privacy: privacy.text.merchant })
          .nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const OutboundShipmentCustomsInfoSchema: z.ZodType<OutboundShipmentCustomsInfo> =
  OutboundShipmentCustomsInfoSchema_;

export interface OutboundShipmentCustomsInfo extends z.infer<
  typeof OutboundShipmentCustomsInfoSchema_
> {}

export enum OutboundShipmentDeliveryConfirmation {
  None = "NO_SIGNATURE",
  Signature = "SIGNATURE",
  Adult_Signature = "ADULT_SIGNATURE",
}

// who pays the taxes and duties on international shipments
// Setting this value to anything other than "DDP" will pass the cost and responsibility of duties on to the recipient of the package
export enum OutboundShipmentIncoterm {
  CFR = "CFR",
  CIF = "CIF",
  CIP = "CIP",
  CPT = "CPT",
  DAT = "DAT",
  DAP = "DAP",
  DDP = "DDP",
  EXW = "EXW",
  FAS = "FAS",
  FCA = "FCA",
  FOB = "FOB",
}

const OutboundShipmentParcelSchema = z
  .object({
    length: z.number().meta({ privacy: privacy.dimension }).nullish(), // inches
    width: z.number().meta({ privacy: privacy.dimension }).nullish(),
    height: z.number().meta({ privacy: privacy.dimension }).nullish(),
    weight: z.number().meta({ privacy: privacy.dimension }), // Ounces
    predefined_package: z
      .string()
      .meta({ privacy: privacy.enumOrStatus })
      .nullish(),
    parcelType: createEnum(CustomParcelType, { enumName: "CustomParcelType" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    additionalHandlingIndicator: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    largePackageIndicator: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    shipperReleaseIndicator: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    customs_info: OutboundShipmentCustomsInfoSchema_.nullish().meta({
      privacy: privacy.structured,
    }),
  })
  .meta({ privacy: privacy.structured });

export interface OutboundShipmentParcel extends z.infer<
  typeof OutboundShipmentParcelSchema
> {}

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const OutboundShipmentParcelKeys = Object.keys(
  OutboundShipmentParcelSchema.shape,
) as (keyof OutboundShipmentParcel)[];

const OutboundShipmentCustomPrintFieldsSchema = z
  .object({
    invoice_numbers: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .meta({ privacy: privacy.structured }), // The sales order number. Should probably be called that
    po_numbers: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.structured }), // Purchase order number
    skus: z
      .array(
        z
          .object({
            sku: z.string().meta({ privacy: privacy.productSku }),
            quantity: z.number().meta({ privacy: privacy.quantity }),
          })
          .meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
    customer_ids: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .meta({ privacy: privacy.structured }),
    // Line-item product/item names to print in the label free-text field
    product_names: z
      .array(z.string().meta({ privacy: privacy.productCatalogText }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    custom_metafield_values: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    // If an order spans multiple packages, which package is this
    package_info: z
      .object({
        package_number: z.number().meta({ privacy: privacy.nonPii }),
        total_packages: z.number().meta({ privacy: privacy.nonPii }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

export interface OutboundShipmentCustomPrintFields extends z.infer<
  typeof OutboundShipmentCustomPrintFieldsSchema
> {}

const outboundShipmentIncotermSchema = createEnum(OutboundShipmentIncoterm, {
  enumName: "OutboundShipmentIncoterm",
}).meta({ privacy: privacy.nonPii });

// Total declared contents of the shipment, derived from order line items.
// Some carriers (e.g. Stallion Express) require a contents declaration on
// every shipment — even domestic — where no customs items are generated.
const outboundShipmentContentsSummarySchema = z
  .object({
    description: z.string().meta({ privacy: privacy.nonPii }),
    value: z.number().meta({ privacy: privacy.nonPii }),
    currency: createEnum(Currency, { enumName: "Currency" }).meta({
      privacy: privacy.nonPii,
    }),
  })
  .meta({ privacy: privacy.structured });

export interface OutboundShipmentContentsSummary extends z.infer<
  typeof outboundShipmentContentsSummarySchema
> {}

/**
 * Carrier item contracts that require integer quantities cannot accept the
 * floating-point drift fulfillment line-item quantities can carry.
 */
export function outboundShipmentItemQuantity(quantity: number): number {
  return Math.max(1, Math.round(quantity));
}

// Provider-specific request inputs, shared by the V1 and V2 request schemas so
// they stay in lockstep. V2 augments only the `easypost` member (see
// createOutboundShipmentRequestV2Schema).
const outboundShipmentRequestProviderInfoSchema = z
  .object({
    easypost: z
      .object({ reference: z.string().meta({ privacy: privacy.nonPii }) })
      .nullish()
      .meta({ privacy: privacy.structured }),
    soapBox: z
      .object({
        batchId: z.string().meta({ privacy: privacy.nonPii }),
        orderId: z.string().meta({ privacy: privacy.nonPii }),
        customBoxId: z.string().meta({ privacy: privacy.nonPii }),
        packageId: z.number().meta({ privacy: privacy.nonPii }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    ups: z
      .object({
        shipperId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        // Skip UPS Simple Rate quoting so only standard UPS rates come back.
        excludeSimpleRate: z
          .boolean()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    landmarkGlobal: z
      .object({
        totalShippingCost: z
          .string()
          .meta({ privacy: privacy.money })
          .nullish(),
        customerEmail: z
          .string()
          .meta({ privacy: privacy.email.customer })
          .nullish(),
        customerPhone: z
          .string()
          .meta({ privacy: privacy.phone.customer })
          .nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    fedex: z
      .object({
        accountNumber: z
          .string()
          .meta({ privacy: privacy.credential })
          .nullish(),
        smartPostHubId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    dhlEcommerce: z
      .object({
        pickupId: z.string().meta({ privacy: privacy.nonPii }),
        distributionCenter: z.string().meta({ privacy: privacy.nonPii }),
        packageId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        packageIdOverride: z
          .string()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
        customLabelText1Override: z
          .string()
          .meta({ privacy: privacy.text.merchant })
          .nullish(),
        customLabelText2Override: z
          .string()
          .meta({ privacy: privacy.text.merchant })
          .nullish(),
        serviceEndorsement: DhlEcommerceServiceEndorsementSchema.nullish().meta(
          { privacy: privacy.nonPii },
        ),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    globale: z
      .object({
        globalEOrderId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        lineItems: z
          .array(
            z
              .object({
                sku: z.string().meta({ privacy: privacy.nonPii }),
                quantity: z.number().meta({ privacy: privacy.nonPii }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .nullish()
          .meta({ privacy: privacy.structured }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    tiktok: z
      .object({
        shippingType: createEnum(TiktokShippingType, {
          enumName: "TiktokShippingType",
        }).meta({ privacy: privacy.nonPii }),
        tiktokOrderIds: z
          .array(z.string().meta({ privacy: privacy.nonPii }))
          .meta({ privacy: privacy.structured }),
        tiktokIntegrationId: z.string().meta({ privacy: privacy.nonPii }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    amazonBuyShipping: z
      .object({
        amazonOrderId: z.string().optional().meta({ privacy: privacy.nonPii }),
        amazonIntegrationId: z.string().meta({ privacy: privacy.nonPii }),
        amazonIntegrationTeamId: z.string().meta({ privacy: privacy.nonPii }),
        shippingBusinessId: z.string().meta({ privacy: privacy.nonPii }),
        items: z
          .array(
            z
              .object({
                amazonOrderItemId: z
                  .string()
                  .optional()
                  .meta({ privacy: privacy.nonPii }),
                quantity: z
                  .number()
                  .meta({ privacy: privacy.nonPii })
                  .positive()
                  .transform(outboundShipmentItemQuantity),
                description: z.string().meta({ privacy: privacy.nonPii }),
                weight: z
                  .object({
                    unit: createEnum(WeightUnit, {
                      enumName: "WeightUnit",
                    }).meta({ privacy: privacy.nonPii }),
                    value: z.number().meta({ privacy: privacy.nonPii }),
                  })
                  .meta({ privacy: privacy.structured }),
                value: MoneySchema.meta({ privacy: privacy.structured }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    amazonShipping: z
      .object({
        shippingBusinessId: z.string().meta({ privacy: privacy.nonPii }),
        items: z
          .array(
            z
              .object({
                quantity: z
                  .number()
                  .meta({ privacy: privacy.nonPii })
                  .positive()
                  .transform(outboundShipmentItemQuantity),
                description: z.string().meta({ privacy: privacy.nonPii }),
                weight: z
                  .object({
                    unit: createEnum(WeightUnit, {
                      enumName: "WeightUnit",
                    }).meta({ privacy: privacy.nonPii }),
                    value: z.number().meta({ privacy: privacy.nonPii }),
                  })
                  .meta({ privacy: privacy.structured }),
                value: MoneySchema.meta({ privacy: privacy.structured }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    uniUni: z
      .object({
        pickupWarehouseId: z.number().meta({ privacy: privacy.nonPii }),
        customerId: z.string().meta({ privacy: privacy.nonPii }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

const outboundShipmentRequestBaseSchema = z
  .object({
    teamId: z.string().meta({ privacy: privacy.id.internal }),
    from_address: outboundShipmentAddressSchema.meta({
      privacy: privacy.structured,
    }),
    to_address: outboundShipmentAddressSchema.meta({
      privacy: privacy.structured,
    }),
    return_address: outboundShipmentAddressSchema
      .nullish()
      .meta({ privacy: privacy.structured }),
    parcels: z
      .array(OutboundShipmentParcelSchema.meta({ privacy: privacy.structured }))
      .meta({ privacy: privacy.structured }),
    is_return: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    skip_one_rate: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    // Redo USPS only
    showRedoLogoOnLabel: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    delivery_confirmation: createEnum(OutboundShipmentDeliveryConfirmation, {
      enumName: "OutboundShipmentDeliveryConfirmation",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    special_rates_eligibility: createEnum(SpecialRateEligibility, {
      enumName: "SpecialRateEligibility",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    label_date: z.coerce.date().nullish(),
    tax_identifiers: z
      .array(OutboundShipmentTaxIdSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    incoterm: outboundShipmentIncotermSchema
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    contents_summary: outboundShipmentContentsSummarySchema
      .nullish()
      .meta({ privacy: privacy.structured }),
    hazmat: HazmatSchema.nullish().meta({ privacy: privacy.nonPii }),
    saturday_delivery: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    customer_account_ids: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.structured }), // Unused, left only for backwards compatibility
    suppressCarrierCommercialInvoice: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    dry_ice: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    dry_ice_weight: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    merchant_id: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    carrier_accounts: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    payment: ShipmentPaymentOptionSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    blocked_providers: z
      .array(
        createEnum(RateProvider, { enumName: "RateProvider" }).meta({
          privacy: privacy.nonPii,
        }),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
    carbon_neutral: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    canada_post_option: createEnum(CanadaPostOption, {
      enumName: "CanadaPostOption",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    direct_delivery_only: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    custom_print_fields: OutboundShipmentCustomPrintFieldsSchema.nullish().meta(
      { privacy: privacy.structured },
    ),
    providerInfo: outboundShipmentRequestProviderInfoSchema
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    duty_payment: LandedCostBillingSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    source: createEnum(OutboundShipmentSource, {
      enumName: "OutboundShipmentSource",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

const OutboundShipmentRequestSchema_ = outboundShipmentRequestBaseSchema.extend(
  {
    // Request overrides keyed by carrier then provider (RateProvider), applied
    // only to that carrier+provider rate sub-request. A carrier can be rated by
    // multiple providers, so the override is scoped to the specific pair.
    // Excluded from OutboundShipmentRequestKeys.
    overrides: z
      .record(
        z.string().meta({ privacy: privacy.nonPii }),
        z.record(
          z.string().meta({ privacy: privacy.nonPii }),
          outboundShipmentRequestBaseSchema.partial(),
        ),
      )
      .nullish(),
  },
);

export const OutboundShipmentRequestSchema: z.ZodType<CreateOutboundShipmentRequest> =
  OutboundShipmentRequestSchema_;

export interface CreateOutboundShipmentRequest extends z.infer<
  typeof OutboundShipmentRequestSchema_
> {}

type OutboundShipmentRequestBase = z.infer<
  typeof outboundShipmentRequestBaseSchema
>;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const OutboundShipmentRequestKeys = Object.keys(
  outboundShipmentRequestBaseSchema.shape,
) as (keyof OutboundShipmentRequestBase)[];

// ---------------------------------------------------------------------------
// Outbound Shipment V2
//
// A fresh request shape for the rebuilt outbound shipping provider. It is
// intentionally NOT derived from the V1 base — it sheds legacy/per-parcel
// concerns and is authored field-by-field. Differences from V1:
//   1. Parcels carry their own units — length/width/height as { unit, value }
//      and weight as { unit, value } — instead of implicit inches/ounces.
//   2. Customs is declared once at the shipment level, not per parcel.
//   3. Tax identifiers are top-level (tax_identifiers) only — they describe the
//      whole shipment, never an individual parcel.
//   4. Hazmat, dry_ice, and dry_ice_weight are declared per parcel, not at the
//      shipment level. dry_ice_weight is unit-aware ({ unit, value }).
//   5. carrier_accounts moves under providerInfo.easypost.carrierAccounts.
//   6. customer_account_ids, skip_one_rate, and blocked_providers are dropped.
// ---------------------------------------------------------------------------

// V2 customs item. Like the V1 item but: no EasyPost `object` tag, weight is
// unit-aware ({ unit, value }) instead of implicit ounces, and unit_value is
// required (V2 always declares the per-unit value alongside the total `value`).
const outboundShipmentCustomsItemV2Schema = z.object({
  description: z.string().meta({ privacy: privacy.nonPii }),
  quantity: z.number().meta({ privacy: privacy.nonPii }),
  origin_country: z.string().meta({ privacy: privacy.nonPii }),
  hs_tariff_number: z.string().meta({ privacy: privacy.nonPii }),
  // Total value of the line (unit_value × quantity).
  total_value: z.string().meta({ privacy: privacy.nonPii }),
  unit_value: z.string().meta({ privacy: privacy.nonPii }),
  currency: createEnum(Currency, { enumName: "Currency" }).nullish(),
  weight: WeightSchema,
  code: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  ...manufacturerCustomsFields,
  product_identifier: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  ...euProductIdentifierFields,
});

export interface OutboundShipmentCustomsItemV2 extends z.infer<
  typeof outboundShipmentCustomsItemV2Schema
> {}

// Shipment-level customs declaration. Mirrors the V1 per-parcel customs_info
// payload (minus EasyPost's `object` field), declared once for the whole
// shipment.
const outboundShipmentCustomsV2Schema = z
  .object({
    contents_type: z.string().meta({ privacy: privacy.nonPii }),
    contents_explanation: z
      .string()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    additional_customs_comments: z
      .string()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    customs_certify: z.boolean().meta({ privacy: privacy.nonPii }),
    customs_signer: z.string().meta({ privacy: privacy.nonPii }),
    eel_pfc: z.string().meta({ privacy: privacy.nonPii }),
    non_delivery_option: z.string().meta({ privacy: privacy.nonPii }),
    restriction_type: z.literal("none").meta({ privacy: privacy.nonPii }),
    customs_items: z
      .array(
        outboundShipmentCustomsItemV2Schema.meta({
          privacy: privacy.structured,
        }),
      )
      .meta({ privacy: privacy.structured }),
    declaration: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  })
  .meta({ privacy: privacy.structured });

export interface OutboundShipmentCustomsV2 extends z.infer<
  typeof outboundShipmentCustomsV2Schema
> {}

const outboundCustomsRequestItemSchema = z.object({
  description: z.string().meta({ privacy: privacy.nonPii }),
  quantity: z.number().meta({ privacy: privacy.nonPii }),
  unit_value: z.string().meta({ privacy: privacy.nonPii }),
  currency: createEnum(Currency, { enumName: "Currency" }),
  unit_weight: WeightSchema,
  origin_country: z.string().meta({ privacy: privacy.nonPii }),
  hs_tariff_number: z.string().meta({ privacy: privacy.nonPii }),
  product_identifier: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  ...euProductIdentifierFields,
  ...manufacturerCustomsFields,
});

export interface OutboundCustomsRequestItem extends z.infer<
  typeof outboundCustomsRequestItemSchema
> {}

/**
 * A customs declaration before the team's settings are applied. Everything the
 * fulfillment-group path derives — certification, signer, EEL/PFC, declaration
 * text, per-line totals and weights — is absent here and filled in by
 * buildOutboundRateRequest, so an API caller cannot disagree with the merchant's
 * own customs configuration.
 */
const outboundCustomsRequestSchema = z
  .object({
    contents_type: createEnum(ContentsType, { enumName: "ContentsType" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    contents_explanation: z
      .string()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    non_delivery_option: createEnum(NonDeliveryOption, {
      enumName: "NonDeliveryOption",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    usps_exemption_code: createEnum(USPSExemptionCode, {
      enumName: "USPSExemptionCode",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    itn: z.string().meta({ privacy: privacy.id.government }).nullish(),
    items: z
      .array(
        outboundCustomsRequestItemSchema.meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

export const OutboundCustomsRequestSchema: z.ZodType<OutboundCustomsRequest> =
  outboundCustomsRequestSchema;
export type OutboundCustomsRequest = z.infer<
  typeof outboundCustomsRequestSchema
>;

const createOutboundShipmentRequestV2BaseSchema = z
  .object({
    teamId: z.string().meta({ privacy: privacy.id.internal }),
    from_address: outboundShipmentAddressSchema.meta({
      privacy: privacy.structured,
    }),
    to_address: outboundShipmentAddressSchema.meta({
      privacy: privacy.structured,
    }),
    return_address: outboundShipmentAddressSchema
      .nullish()
      .meta({ privacy: privacy.structured }),
    parcels: z
      .array(
        OutboundShipmentParcelV2Schema.meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
    customs: outboundShipmentCustomsV2Schema
      .nullish()
      .meta({ privacy: privacy.structured }),
    is_return: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    showRedoLogoOnLabel: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    delivery_confirmation: createEnum(OutboundShipmentDeliveryConfirmation, {
      enumName: "OutboundShipmentDeliveryConfirmation",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    special_rates_eligibility: createEnum(SpecialRateEligibility, {
      enumName: "SpecialRateEligibility",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    // coerce: the V2 request is persisted as JSONB in the `createOutboundShipmentRequest`
    // column, so a Date round-trips back as an ISO string and must re-parse into a Date.
    label_date: z.coerce.date().nullish(),
    tax_identifiers: z
      .array(OutboundShipmentTaxIdSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    incoterm: outboundShipmentIncotermSchema
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    contents_summary: outboundShipmentContentsSummarySchema
      .nullish()
      .meta({ privacy: privacy.structured }),
    saturday_delivery: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    suppressCarrierCommercialInvoice: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    merchant_id: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    payment: ShipmentPaymentOptionSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    carbon_neutral: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    canada_post_option: createEnum(CanadaPostOption, {
      enumName: "CanadaPostOption",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    direct_delivery_only: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    custom_print_fields: OutboundShipmentCustomPrintFieldsSchema.nullish().meta(
      { privacy: privacy.structured },
    ),
    // Mirrors the V1 request providerInfo, with carrierAccounts added to easypost
    // (carrier_accounts is no longer a top-level field in V2).
    providerInfo: outboundShipmentRequestProviderInfoSchema
      .extend({
        // reference is nullish in V2 (required in V1's easypost) so the resolved
        // carrier accounts can be set without a reference at rate time.
        easypost: z
          .object({
            reference: z.string().meta({ privacy: privacy.nonPii }).nullish(),
            carrierAccounts: z
              .array(z.string().meta({ privacy: privacy.nonPii }))
              .nullish(),
          })
          .nullish(),
      })
      .nullish(),
    duty_payment: LandedCostBillingSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    source: createEnum(OutboundShipmentSource, {
      enumName: "OutboundShipmentSource",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

// A V2 rate sub-request override, scoped to a carrier + RateProvider pair. Only
// the incoterm is overridable for now: USPS and International Bridge only quote
// DAP, so an LCG shipment forced to DDP downgrades just those sub-requests back
// to DAP. Unlike the V1 request's free-form partial-request override, this is a
// single typed field — which keeps the V1 mapping trivial.
const outboundShipmentRequestV2OverridesSchema = z.partialRecord(
  createEnum(Carriers, { enumName: "Carriers" }),
  z.partialRecord(
    createEnum(RateProvider, { enumName: "RateProvider" }),
    z.object({ incoterm: outboundShipmentIncotermSchema }),
  ),
);

export const createOutboundShipmentRequestV2Schema =
  createOutboundShipmentRequestV2BaseSchema.extend({
    // Per carrier+provider rate sub-request overrides. Only the incoterm is
    // overridable (see outboundShipmentRequestV2OverridesSchema). Excluded from
    // CreateOutboundShipmentRequestV2Keys.
    overrides: outboundShipmentRequestV2OverridesSchema.nullish(),
  });

export const CreateOutboundShipmentRequestV2Schema: z.ZodType<CreateOutboundShipmentRequestV2> =
  createOutboundShipmentRequestV2Schema;

export type CreateOutboundShipmentRequestV2 = z.infer<
  typeof createOutboundShipmentRequestV2Schema
>;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const CreateOutboundShipmentRequestV2Keys = Object.keys(
  createOutboundShipmentRequestV2BaseSchema.shape,
) as (keyof CreateOutboundShipmentRequestV2)[];

// providerInfo is resolved server-side (UPS ghost account, FedEx/DHL/UniUni
// config); is_return is forced false (outbound-only). `customs` is replaced by
// `customsRequest` — the caller declares the goods and buildOutboundRateRequest
// expands it against team settings, so the API and the fulfillment-group path
// produce the same customs form. Everything else is caller input.
const outboundRateRequestSchema = createOutboundShipmentRequestV2Schema
  .omit({ providerInfo: true, is_return: true, customs: true })
  .extend({ customsRequest: outboundCustomsRequestSchema.nullish() });

export const OutboundRateRequestSchema: z.ZodType<OutboundRateRequest> =
  outboundRateRequestSchema;
export type OutboundRateRequest = z.infer<typeof outboundRateRequestSchema>;

export enum OutboundShipmentPurchaseStatus {
  PURCHASED = "purchased",
  FAILED = "failed",
}

const outboundShipmentPackageSchema = z
  .object({
    labelUrl: z.string().meta({ privacy: privacy.privateUrl }),
    trackingCode: z.string().meta({ privacy: privacy.id.tracking }),
    // Carrier's public tracking page for this package, populated at buy.
    trackingUrl: z.string().meta({ privacy: privacy.privateUrl }).nullish(),
    // This package's share of the price (IMoney) and its dimensions/weight
    // (unit-aware), populated at buy.
    rate: MoneySchema.nullish().meta({ privacy: privacy.structured }),
    provider_cost: MoneySchema.nullish().meta({ privacy: privacy.structured }),
    length: LengthSchema.nullish().meta({ privacy: privacy.structured }),
    width: LengthSchema.nullish().meta({ privacy: privacy.structured }),
    height: LengthSchema.nullish().meta({ privacy: privacy.structured }),
    weight: WeightSchema.nullish().meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export type OutboundShipmentPackage = z.infer<
  typeof outboundShipmentPackageSchema
>;

/**
 * Buy-time snapshot of the purchased rate, written by savePurchasedRateInfo.
 * Field names mirror OutboundRate; `service` is a string — parse as needed.
 */
const purchasedRateInfoSchema = z
  .object({
    rate: MoneySchema.meta({ privacy: privacy.structured }),
    provider_cost: MoneySchema.nullish().meta({ privacy: privacy.structured }),
    carrier: CarrierSchema.meta({ privacy: privacy.nonPii }),
    service: z.string().meta({ privacy: privacy.nonPii }),
    carrier_account_id: z.string().meta({ privacy: privacy.nonPii }),
    billing_type: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    is_merchant_carrier_account: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    is_parent_easypost_account: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    payment_option: z
      .custom<ShipmentPaymentOption>()
      .meta({ annotation: "JSON", privacy: privacy.untypedPayload })
      .nullish(),
    providerInfo: z
      .custom<ProviderInfo>()
      .meta({ annotation: "JSON", privacy: privacy.untypedPayload })
      .nullish(),
  })
  .meta({ privacy: privacy.structured });

const OutboundShipmentSchema_ = z
  .object({
    _id: zExt.objectId().meta({ privacy: privacy.nonPii }),
    team: zExt.objectId().meta({ privacy: privacy.nonPii }),
    createdAt: z.coerce.date().meta({ privacy: privacy.nonPii }),
    updatedAt: z.coerce.date().meta({ privacy: privacy.nonPii }),
    redoShipmentId: z.string().meta({ privacy: privacy.nonPii }),
    providerBoughtShipmentId: z
      .string()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    trackingNumber: z.string().meta({ privacy: privacy.id.tracking }).nullish(),
    purchaseStatus: createEnum(OutboundShipmentPurchaseStatus, {
      enumName: "OutboundShipmentPurchaseStatus",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    refundStatus: createEnum(ShipmentRefundStatus, {
      enumName: "ShipmentRefundStatus",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    // Holds either the V1 or the V2 request. Stored opaquely as a single JSONB
    // column (the `annotation: "JSON"` keeps the CongoDB schema extractor from
    // table-splitting the union). The repo accessors re-parse it with the precise
    // V1 / V2 schema, so callers never see the raw union — see
    // OutboundShipmentV1Schema / OutboundShipmentV2Schema and findOutboundShipment
    // / findV2OutboundShipment.
    createOutboundShipmentRequest: z
      .custom<CreateOutboundShipmentRequest | CreateOutboundShipmentRequestV2>()
      .meta({ annotation: "JSON", privacy: privacy.untypedPayload }),
    // Set on per-package child shipments (V2): points at the parent's
    // redoShipmentId. Null on V1 shipments and on V2 parents.
    parentShipmentId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    kind: createEnum(OutboundShipmentKind, { enumName: "OutboundShipmentKind" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    shipmentPackages: z
      .array(
        outboundShipmentPackageSchema.meta({ privacy: privacy.structured }),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
    /** @deprecated Read-only for legacy docs; new buys write purchasedRateInfo.rate. */
    rate: MoneySchema.nullish().meta({ privacy: privacy.structured }),
    /** @deprecated Read-only for legacy docs; new buys write purchasedRateInfo.provider_cost. */
    provider_cost: MoneySchema.nullish().meta({ privacy: privacy.structured }),
    // Explicit V1/V2 discriminator. 1 (or absent, on legacy docs) ⇒ V1; 2 ⇒ V2.
    // The repo narrows on this rather than on the request's shape — see
    // OutboundShipmentV1Schema / OutboundShipmentV2Schema.
    version: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    provider: createEnum(RateProvider, { enumName: "RateProvider" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    // Buy-time snapshot of the purchased rate, so charge and void read only the doc.
    purchasedRateInfo: purchasedRateInfoSchema
      .nullish()
      .meta({ privacy: privacy.structured }),
    // Buy-time customs docs for the whole shipment (populated on international buys).
    commercialInvoiceUrl: z
      .string()
      .meta({ privacy: privacy.privateUrl })
      .nullish(),
    comInvoiceSubmittedElectronically: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    /** @deprecated Write-only debug data; no longer persisted (we store []). */
    rates: z
      .array(rawOutboundRateSchema.meta({ privacy: privacy.structured }))
      .meta({ privacy: privacy.structured }),
    // Raw provider data for debugging/auditing - stored but excluded from reads
    rawProviderRatingRequests: z
      .array(z.any().meta({ privacy: privacy.untypedPayload }))
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    rawProviderRatingResponses: z
      .array(z.any().meta({ privacy: privacy.untypedPayload }))
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    rawProviderPurchaseRequest: z
      .any()
      .meta({ privacy: privacy.untypedPayload })
      .nullish(),
    rawProviderPurchaseResponse: z
      .any()
      .meta({ privacy: privacy.untypedPayload })
      .nullish(),
    rawProviderVoidResponse: z
      .any()
      .meta({ privacy: privacy.untypedPayload })
      .nullish(),
    rawVoidResponse: z
      .any()
      .meta({ privacy: privacy.untypedPayload })
      .nullish(),
    cleanedAt: z.coerce.date().nullish().meta({ privacy: privacy.nonPii }),
    source: createEnum(OutboundShipmentSource, {
      enumName: "OutboundShipmentSource",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
// The stored shape: `createOutboundShipmentRequest` is the raw V1-or-V2 union.
// Used only at the persistence boundary (the repo + CongoDB extraction); app
// code uses the narrowed OutboundShipment / OutboundShipmentV2 below.
export const OutboundShipmentSchema: z.ZodType<OutboundShipmentStored> =
  OutboundShipmentSchema_;

export interface OutboundShipmentStored extends z.infer<
  typeof OutboundShipmentSchema_
> {}

export type PurchasedRateInfo = z.infer<typeof purchasedRateInfoSchema>;

// Buy-time customs docs snapshotted onto the shipment doc, like PurchasedRateInfo.
export type PurchaseCustomsDocs = Pick<
  OutboundShipmentStored,
  "commercialInvoiceUrl" | "comInvoiceSubmittedElectronically"
>;

type OutboundShipmentCommon = Omit<
  OutboundShipmentStored,
  "createOutboundShipmentRequest"
>;

// The canonical (V1) outbound shipment — the shape every existing caller uses.
// `createOutboundShipmentRequest` is narrowed to the V1 request; the repo
// asserts this when reading (findOutboundShipment), so callers are unchanged.
export interface OutboundShipment extends OutboundShipmentCommon {
  createOutboundShipmentRequest: CreateOutboundShipmentRequest;
  version?: 1 | null;
}

// A V2 outbound shipment (parent or per-package child). Same document shape,
// but `createOutboundShipmentRequest` is the V2 request. Read via
// findV2OutboundShipment, which asserts the V2 shape.
export interface OutboundShipmentV2 extends OutboundShipmentCommon {
  createOutboundShipmentRequest: CreateOutboundShipmentRequestV2;
  version: 2;
}

// Parse schemas that narrow the stored union to a concrete request shape. These
// are NOT registered for CongoDB extraction (only OutboundShipmentSchema is);
// they exist purely so the repo can validate-and-narrow on read/write.
const OutboundShipmentV1Schema_ = OutboundShipmentSchema_.extend({
  createOutboundShipmentRequest: OutboundShipmentRequestSchema_,
  // V1 docs are version 1 or, on legacy documents, have no version at all.
  version: z.literal(1).nullish(),
});
export const OutboundShipmentV1Schema: z.ZodType<OutboundShipment> =
  OutboundShipmentV1Schema_;

const OutboundShipmentV2Schema_ = OutboundShipmentSchema_.extend({
  createOutboundShipmentRequest: createOutboundShipmentRequestV2Schema,
  version: z.literal(2),
});
export const OutboundShipmentV2Schema: z.ZodType<OutboundShipmentV2> =
  OutboundShipmentV2Schema_;

// Version-agnostic projection: omits `version`/`createOutboundShipmentRequest`,
// so it parses V1, V2, and wrap-v1-under-V2 carrier shipments uniformly.
const outboundShipmentViewSchema = OutboundShipmentSchema_.pick({
  _id: true,
  team: true,
  createdAt: true,
  updatedAt: true,
  redoShipmentId: true,
  parentShipmentId: true,
  kind: true,
  version: true,
  provider: true,
  purchaseStatus: true,
  refundStatus: true,
  providerBoughtShipmentId: true,
  trackingNumber: true,
  rate: true,
  provider_cost: true,
  purchasedRateInfo: true,
  commercialInvoiceUrl: true,
  comInvoiceSubmittedElectronically: true,
  shipmentPackages: true,
  source: true,
});
export type OutboundShipmentView = z.infer<typeof outboundShipmentViewSchema>;
export const OutboundShipmentViewSchema: z.ZodType<OutboundShipmentView> =
  outboundShipmentViewSchema;

export function joinOrderNames(
  orderNames: CreateOutboundShipmentRequest["custom_print_fields"],
) {
  if (!orderNames) {
    return "";
  }
  const uniqueOrderNames = new Set<string>(orderNames.invoice_numbers);
  return Array.from(uniqueOrderNames).join(", ");
}

export function joinPoNumbers(
  customPrintFields: CreateOutboundShipmentRequest["custom_print_fields"],
) {
  if (!customPrintFields?.po_numbers) {
    return "";
  }
  const uniquePoNumbers = new Set<string>(customPrintFields.po_numbers);
  return Array.from(uniquePoNumbers).join(", ");
}

export function joinOrderAndPoNumbers(
  customPrintFields: CreateOutboundShipmentRequest["custom_print_fields"],
) {
  const orderNames = joinOrderNames(customPrintFields);
  const poNumbers = joinPoNumbers(customPrintFields);
  if (orderNames && poNumbers) {
    return `${orderNames} - ${poNumbers}`;
  }
  return orderNames || poNumbers;
}

export function buildSkuList(
  skus: OutboundShipmentCustomPrintFields["skus"],
): string[] {
  return skus.map((sku) =>
    sku.quantity === 1 ? sku.sku : `${sku.sku} x${sku.quantity}`,
  );
}

// Normalize line-item names for the label free-text field: trim, drop blanks,
// and dedupe while preserving order. Characters pass through as-is; any
// per-carrier line-length truncation is applied by the provider.
export function buildProductNameList(
  productNames: readonly string[],
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const name of productNames) {
    const trimmed = name.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      result.push(trimmed);
    }
  }
  return result;
}

// Matches a post office box however the customer punctuated it — "PO Box",
// "P.O.Box", "P. O. Box", "POBox", "P  O  Box" — including the forms that omit
// the word entirely ("PO 979", "PO# 136", "po745").
//
// `[a-z]?` admits the prefixed box types: APO/DPO/FPO (US military), GPO
// (General Post Office; AU/NZ/HK), LPO (Licensed Post Office; AU). The leading
// boundary keeps the pattern off letter-runs that merely contain "po" ("Depot
// St", "Campo Boxwood Ln", "Expo Center Dr").
//
// Each separator is one character class, and the branches after it start with
// characters outside that class, so every quantifier has a single viable split
// and matching stays linear. Branches that begin with a separator (`\s+box`,
// `[\s.]*#?`) let one run of whitespace be divided among several quantifiers
// and backtrack cubically.
const PO_BOX_PATTERN = /(?:^|[^a-z0-9])[a-z]?p[\s.]*o[\s.]*(?:box|#|\d)/i;

export function isPoBoxAddress({
  line1,
  line2,
}: {
  line1?: string | null;
  line2?: string | null;
}): boolean {
  return [line1, line2].some((line) => !!line && PO_BOX_PATTERN.test(line));
}

export function formatReference(input: string, maxLength: number): string {
  // Keep only letters, numbers, commas, dashes, and # signs. Don't go over the max length.
  return input.replace(/[^a-zA-Z0-9,#-]/g, " ").slice(0, maxLength);
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { OutboundShipmentKind } from "@redotech/congo-enums/outbound-shipment-kind";
